/**
 * BulkSetFieldModal — set one classification field (category, altitude,
 * source, strategic bucket, dedupe status) to one value on every selected
 * item. The choices come from the list's own field catalog, so the bar and
 * the filter panel always agree on what a value is called.
 */
import { useMemo, useState } from 'react';
import { Modal, Select, Field, Callout } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { ITEM_FIELD_BY_KEY } from '../item-list/config/itemFields';
import type { Item } from '../../../types/prioritization/lifecycle';
import { itemsWord, runBulk, type BulkActionResult } from './index';

export interface BulkSetFieldModalProps {
  open: boolean;
  selectedIds: string[];
  onClose: () => void;
  onComplete: (result: BulkActionResult) => void;
}

interface BulkField {
  /** Key in the list's field catalog (ITEM_FIELD_BY_KEY). */
  catalogKey: string;
  /** Property written on the item. */
  itemField: keyof Item;
  label: string;
  numeric?: boolean;
  /** Values that need more than a bulk set can give (a duplicate needs its original). */
  exclude?: string[];
}

const FIELDS: BulkField[] = [
  { catalogKey: 'itemCategory', itemField: 'itemCategory', label: 'Category' },
  { catalogKey: 'altitude', itemField: 'altitudeLevel', label: 'Altitude', numeric: true },
  { catalogKey: 'source', itemField: 'source', label: 'Source' },
  { catalogKey: 'strategicBucket', itemField: 'strategicBucket', label: 'Strategic bucket' },
  { catalogKey: 'dedupeStatus', itemField: 'dedupeStatus', label: 'Dedupe status', exclude: ['duplicate_of'] },
];

export function BulkSetFieldModal({ open, selectedIds, onClose, onComplete }: BulkSetFieldModalProps) {
  const [fieldKey, setFieldKey] = useState<string>(FIELDS[0].catalogKey);
  const [value, setValue] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const items = useItemsStore((s) => s.items);
  const updateItem = useItemsStore((s) => s.updateItem);

  const field = FIELDS.find((f) => f.catalogKey === fieldKey) ?? FIELDS[0];
  const options = useMemo(
    () =>
      (ITEM_FIELD_BY_KEY[field.catalogKey]?.filter?.options ?? []).filter(
        (o) => !(field.exclude ?? []).includes(o.value),
      ),
    [field],
  );
  const valueLabel = options.find((o) => o.value === value)?.label;
  const count = selectedIds.length;

  const apply = async () => {
    if (!value || busy) return;
    const next: unknown = field.numeric ? Number(value) : value;
    setBusy(true);
    try {
      const result = await runBulk(
        `${field.label} set`,
        selectedIds,
        (id) => items.find((i) => i.id === id)?.title ?? id,
        async (id) => {
          const item = items.find((i) => i.id === id);
          if (!item) return { ok: false, reason: 'Item not found' };
          if ((item as unknown as Record<string, unknown>)[field.itemField] === next) return { ok: true };
          return updateItem(id, { [field.itemField]: next } as Partial<Item>);
        },
        (done, total) => setProgress({ done, total }),
      );
      onComplete(result);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      dismissible={!busy}
      size="sm"
      title={`Set a field on ${itemsWord(count)}`}
      description="One field, one value, every selected item."
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="cta" size="sm" onClick={() => void apply()} disabled={busy || !value}>
            {busy && progress ? `Saving… ${progress.done} of ${progress.total}` : `Set on ${itemsWord(count)}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Field">
          <Select
            size="sm"
            value={fieldKey}
            onChange={(e) => { setFieldKey(e.target.value); setValue(''); }}
            disabled={busy}
            autoFocus
            options={FIELDS.map((f) => ({ value: f.catalogKey, label: f.label }))}
          />
        </Field>
        <Field label="Value">
          <Select
            size="sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={busy}
            placeholder="Choose a value…"
            options={options.map((o) => ({ value: o.value, label: o.label }))}
          />
        </Field>
        {value && (
          <Callout tone="slate">
            You are about to set <strong className="text-[var(--text-pri)]">{field.label}</strong> to{' '}
            <strong className="text-[var(--text-pri)]">{valueLabel ?? value}</strong> on {itemsWord(count)}.
          </Callout>
        )}
      </div>
    </Modal>
  );
}
